import { Plus, Sun, Moon, Settings, Brain, Upload, Download, AlertTriangle } from 'lucide-react';
import packageJson from '../../package.json';
import appIcon from '../assets/icon_64.png';

/**
 * Sticky header bar with logo and action buttons.
 * Extracted from TradingJournal.js (Phase 5).
 *
 * Note (v7.6.2): a short-lived session-mode pill was added then removed
 * here when we realized mode belongs to the account, not the session.
 * Multi-account prop traders run test + production accounts simultaneously;
 * a global mode toggle would mis-tag trades across accounts. Mode now
 * lives on `account.mode` (see AddEditAccountModal.js); the trade
 * inherits its account's mode at creation time.
 */
const HeaderNav = ({
  darkMode,
  headerScrolled,
  // Action callbacks
  onAIInsights,
  onToggleDarkMode,
  onImportClick,
  onShowSync,
  onShowSettings,
  onAddTrade,
  // AI insights state
  tradesCount,
  // JournifiSync state
  journifiSync,
  // Phase 1A — "Needs Backfill" pill. Counts trades missing latest bar-
  // analysis fields (Wave A/B/C/D/E). Click routes to Settings → Storage
  // and scrolls to the backfill section. Hidden when count is 0 so the
  // header stays clean for users with fully-backfilled data.
  needsBackfillCount = 0,
  onClickNeedsBackfill,
}) => {
  return (
    <>
      {/* Sticky Header Bar */}
      <div
        className="sticky top-0 z-50"
        style={{
          transition: 'box-shadow 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: headerScrolled ? '0 4px 24px rgba(0,0,0,0.3)' : 'none'
        }}
      >
        <div
          className="mx-auto max-w-[2400px]"
          style={{
            transition: 'padding 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            paddingLeft: headerScrolled ? 16 : 24,
            paddingRight: headerScrolled ? 16 : 24,
            paddingTop: headerScrolled ? 4 : 32,
            paddingBottom: 0
          }}
        >
          <div
            className={darkMode ? 'border-white/10' : 'border-white/30'}
            style={{
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              borderRadius: headerScrolled ? '0 0 12px 12px' : '16px 16px 0 0',
              borderWidth: 1,
              borderStyle: 'solid',
              borderBottomWidth: headerScrolled ? 1 : 0,
              borderTopWidth: headerScrolled ? 0 : 1,
              background: darkMode
                ? headerScrolled ? 'rgba(0,0,0,0.85)' : 'rgba(0,0,0,0.45)'
                : headerScrolled ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.3)',
              backdropFilter: headerScrolled ? 'blur(24px) saturate(1.2)' : 'blur(12px)',
              WebkitBackdropFilter: headerScrolled ? 'blur(24px) saturate(1.2)' : 'blur(12px)',
              padding: headerScrolled ? '8px 16px' : '24px 24px 16px'
            }}
          >
            <div className="flex justify-between items-center">
              <div className="flex items-center" style={{ transition: 'gap 0.4s cubic-bezier(0.4, 0, 0.2, 1)', gap: 12 }}>
                {/* Journifi Logo */}
                <img
                  src={appIcon}
                  alt="Journifi"
                  className="rounded-lg shadow-lg"
                  style={{
                    transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1), height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    width: headerScrolled ? 28 : 40,
                    height: headerScrolled ? 28 : 40
                  }}
                />
                <div style={{ overflow: 'hidden' }}>
                  <h1
                    className={`font-bold whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-800'}`}
                    style={{
                      transition: 'font-size 0.4s cubic-bezier(0.4, 0, 0.2, 1), line-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      fontSize: headerScrolled ? 18 : 30,
                      lineHeight: headerScrolled ? '28px' : '36px'
                    }}
                  >
                    Journifi AI
                    <span
                      className={`ml-3 font-normal ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}
                      style={{
                        transition: 'font-size 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        fontSize: headerScrolled ? 11 : 14
                      }}
                    >
                      v{packageJson.version}
                    </span>
                  </h1>
                  {/* Subtitle - always rendered, animated with height + opacity */}
                  <div
                    style={{
                      transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), margin-top 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      maxHeight: headerScrolled ? 0 : 24,
                      opacity: headerScrolled ? 0 : 1,
                      marginTop: headerScrolled ? 0 : 2,
                      overflow: 'hidden'
                    }}
                  >
                    <p className={`text-sm whitespace-nowrap ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      AI-Powered Trading Journal
                      <span className={`ml-3 text-[10px] italic ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>by AlphaWolfTrader</span>
                    </p>
                  </div>
                </div>
              </div>
              <div
                className="flex items-center"
                style={{
                  transition: 'gap 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  gap: headerScrolled ? 8 : 16
                }}
              >
                {/* Frosted-glass action buttons — each keeps its color identity
                    (purple/blue/gray/green) but with a translucent tint +
                    matching border + soft hover glow so they read as glass
                    chips, not flat saturated rectangles. Matches the SlicerBar
                    pill / SaaS-era panel aesthetic used elsewhere. */}
                <button
                  onClick={onAIInsights}
                  className={`rounded-lg flex items-center gap-2 border transition-[background-color,border-color,box-shadow,color] duration-150 disabled:opacity-40 disabled:cursor-not-allowed ${
                    darkMode
                      ? 'bg-purple-500/20 border-purple-400/40 text-purple-100 hover:bg-purple-500/30 hover:border-purple-400/60 hover:shadow-[0_0_18px_rgba(168,85,247,0.25)]'
                      : 'bg-purple-100 border-purple-300 text-purple-800 hover:bg-purple-200 hover:border-purple-400'
                  }`}
                  style={{
                    transition: 'padding 0.4s cubic-bezier(0.4, 0, 0.2, 1), font-size 0.4s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s, border-color 0.2s, box-shadow 0.2s',
                    padding: headerScrolled ? '6px 12px' : '8px 16px',
                    fontSize: headerScrolled ? 13 : 14
                  }}
                  disabled={tradesCount < 5}
                  title={tradesCount < 5 ? "Need at least 5 trades for AI analysis" : ""}
                >
                  <Brain style={{ transition: 'width 0.4s, height 0.4s', width: headerScrolled ? 16 : 20, height: headerScrolled ? 16 : 20 }} />
                  AI Insights
                </button>
                <button
                  onClick={onToggleDarkMode}
                  className={`rounded-lg border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                    darkMode
                      ? 'bg-amber-500/15 border-amber-400/30 text-amber-300 hover:bg-amber-500/25 hover:border-amber-400/50 hover:shadow-[0_0_16px_rgba(251,191,36,0.25)]'
                      : 'bg-indigo-100 border-indigo-300 text-indigo-700 hover:bg-indigo-200 hover:border-indigo-400'
                  }`}
                  style={{
                    transition: 'padding 0.4s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s, border-color 0.2s, box-shadow 0.2s',
                    padding: headerScrolled ? 6 : 8
                  }}
                  title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                >
                  {darkMode
                    ? <Sun style={{ transition: 'width 0.4s, height 0.4s', width: headerScrolled ? 16 : 20, height: headerScrolled ? 16 : 20 }} />
                    : <Moon style={{ transition: 'width 0.4s, height 0.4s', width: headerScrolled ? 16 : 20, height: headerScrolled ? 16 : 20 }} />
                  }
                </button>
                {/* Phase 1A — "Needs Backfill" pill. Surfaces hidden Settings
                    affordance: clicking jumps to Settings → Storage → Backfill.
                    Renders only when there are eligible trades missing latest
                    bar-analysis fields. Amber to match warning tone but
                    intentionally subtle (not red) — backfill is opportunity,
                    not failure. */}
                {needsBackfillCount > 0 && (
                  <button
                    onClick={onClickNeedsBackfill}
                    className={`flex items-center gap-1.5 rounded-lg border transition-colors ${
                      darkMode
                        ? 'bg-amber-500/15 border-amber-400/40 text-amber-200 hover:bg-amber-500/25 hover:border-amber-400/60'
                        : 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100 hover:border-amber-400'
                    }`}
                    style={{
                      transition: 'padding 0.4s cubic-bezier(0.4, 0, 0.2, 1), font-size 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      padding: headerScrolled ? '6px 10px' : '8px 12px',
                      fontSize: headerScrolled ? 12 : 13,
                    }}
                    title={`${needsBackfillCount} trade${needsBackfillCount === 1 ? '' : 's'} ${needsBackfillCount === 1 ? 'is' : 'are'} missing latest bar-analysis fields. Click to backfill — unlocks Pattern / Discipline / Trend filters in the Slicer.`}
                  >
                    <AlertTriangle style={{ transition: 'width 0.4s, height 0.4s', width: headerScrolled ? 13 : 15, height: headerScrolled ? 13 : 15 }} />
                    <span className="tabular-nums font-semibold">{needsBackfillCount}</span>
                    <span>need backfill</span>
                  </button>
                )}
                <button
                  onClick={onImportClick}
                  className={`rounded-lg flex items-center gap-2 border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                    darkMode
                      ? 'bg-blue-500/20 border-blue-400/40 text-blue-100 hover:bg-blue-500/30 hover:border-blue-400/60 hover:shadow-[0_0_18px_rgba(59,130,246,0.25)]'
                      : 'bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200 hover:border-blue-400'
                  }`}
                  style={{
                    transition: 'padding 0.4s cubic-bezier(0.4, 0, 0.2, 1), font-size 0.4s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s, border-color 0.2s, box-shadow 0.2s',
                    padding: headerScrolled ? '6px 12px' : '8px 16px',
                    fontSize: headerScrolled ? 13 : 14
                  }}
                >
                  <Upload style={{ transition: 'width 0.4s, height 0.4s', width: headerScrolled ? 16 : 20, height: headerScrolled ? 16 : 20 }} />
                  Import
                </button>
                <button
                  onClick={onShowSync}
                  className={`rounded-lg flex items-center gap-2 border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                    darkMode
                      ? 'bg-fuchsia-500/20 border-fuchsia-400/40 text-fuchsia-100 hover:bg-fuchsia-500/30 hover:border-fuchsia-400/60 hover:shadow-[0_0_18px_rgba(217,70,239,0.25)]'
                      : 'bg-fuchsia-100 border-fuchsia-300 text-fuchsia-800 hover:bg-fuchsia-200 hover:border-fuchsia-400'
                  }`}
                  style={{
                    transition: 'padding 0.4s cubic-bezier(0.4, 0, 0.2, 1), font-size 0.4s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s, border-color 0.2s, box-shadow 0.2s',
                    padding: headerScrolled ? '6px 12px' : '8px 16px',
                    fontSize: headerScrolled ? 13 : 14
                  }}
                  title={journifiSync.isActive
                    ? `JournifiSync Active${journifiSync.ninjatraderConnected ? ' - NinjaTrader Connected' : ' - NinjaTrader Not Connected'}${journifiSync.lastSyncTime ? ` - Last sync: ${journifiSync.lastSyncTime.toLocaleTimeString()}` : ''}`
                    : 'Import from NinjaTrader via JournifiSync'}
                >
                  {/* Recessed Status Indicator */}
                  <span
                    className={`w-3 h-3 rounded-full transition-[background-color,border-color,box-shadow,color] duration-150 ${
                      journifiSync.status?.error
                        ? 'bg-red-500 shadow-[inset_0_1px_3px_rgba(0,0,0,0.4)]'
                        : journifiSync.isActive
                          ? journifiSync.ninjatraderConnected
                            ? 'bg-green-400 shadow-[inset_0_1px_3px_rgba(0,0,0,0.3),0_0_6px_rgba(74,222,128,0.5)]'
                            : 'bg-amber-400 shadow-[inset_0_1px_3px_rgba(0,0,0,0.3),0_0_6px_rgba(251,191,36,0.5)]'
                          : 'bg-gray-600 shadow-[inset_0_2px_4px_rgba(0,0,0,0.5)]'
                    }`}
                    style={{
                      boxShadow: journifiSync.status?.error
                        ? 'inset 0 1px 3px rgba(0,0,0,0.4), 0 0 6px rgba(239,68,68,0.5)'
                        : journifiSync.isActive
                          ? journifiSync.ninjatraderConnected
                            ? 'inset 0 1px 2px rgba(0,0,0,0.3), 0 0 8px rgba(74,222,128,0.6)'
                            : 'inset 0 1px 2px rgba(0,0,0,0.3), 0 0 8px rgba(251,191,36,0.6)'
                          : 'inset 0 2px 4px rgba(0,0,0,0.6)'
                    }}
                  />
                  <Download style={{ transition: 'width 0.4s, height 0.4s', width: headerScrolled ? 16 : 20, height: headerScrolled ? 16 : 20 }} />
                  <span className="flex flex-col items-start leading-tight">
                    <span>Sync</span>
                    {journifiSync.isActive && journifiSync.lastSyncTime && (
                      <span
                        className="text-[10px]"
                        style={{
                          transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          opacity: headerScrolled ? 0 : 0.75,
                          maxHeight: headerScrolled ? 0 : 16,
                          overflow: 'hidden',
                          display: 'block'
                        }}
                      >
                        {(() => {
                          const now = new Date();
                          const diff = Math.floor((now - journifiSync.lastSyncTime) / 1000);
                          if (diff < 60) return `${diff}s ago`;
                          if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
                          return journifiSync.lastSyncTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                        })()}
                      </span>
                    )}
                  </span>
                </button>
                <button
                  onClick={onShowSettings}
                  className={`rounded-lg flex items-center gap-2 border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                    darkMode
                      ? 'bg-white/[0.06] border-white/15 text-gray-200 hover:bg-white/10 hover:border-white/25 hover:shadow-[0_0_14px_rgba(255,255,255,0.08)]'
                      : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200 hover:border-gray-400'
                  }`}
                  style={{
                    transition: 'padding 0.4s cubic-bezier(0.4, 0, 0.2, 1), font-size 0.4s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s, border-color 0.2s, box-shadow 0.2s',
                    padding: headerScrolled ? '6px 12px' : '8px 16px',
                    fontSize: headerScrolled ? 13 : 14
                  }}
                >
                  <Settings style={{ transition: 'width 0.4s, height 0.4s', width: headerScrolled ? 16 : 20, height: headerScrolled ? 16 : 20 }} />
                  Settings
                </button>
                {/* Add Trade — primary CTA. Slightly more saturated tint +
                    inner ring so it remains the brightest button on the bar
                    while still reading as glass, not flat green. */}
                <button
                  onClick={onAddTrade}
                  className={`rounded-lg flex items-center gap-2 border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                    darkMode
                      ? 'bg-green-500/25 border-green-400/50 text-green-100 ring-1 ring-inset ring-green-400/20 hover:bg-green-500/35 hover:border-green-400/70 hover:shadow-[0_0_22px_rgba(34,197,94,0.35)]'
                      : 'bg-green-100 border-green-300 text-green-800 hover:bg-green-200 hover:border-green-400'
                  }`}
                  style={{
                    transition: 'padding 0.4s cubic-bezier(0.4, 0, 0.2, 1), font-size 0.4s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s, border-color 0.2s, box-shadow 0.2s',
                    padding: headerScrolled ? '6px 12px' : '8px 16px',
                    fontSize: headerScrolled ? 13 : 14
                  }}
                >
                  <Plus style={{ transition: 'width 0.4s, height 0.4s', width: headerScrolled ? 16 : 20, height: headerScrolled ? 16 : 20 }} />
                  Add Trade
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

    </>
  );
};

export default HeaderNav;
